import * as React from 'react';
import { UIHeading1 } from '@cian/ui-kit';

import { useFloatingHeader } from '../NewbuildingsComparePage/hooks/useFloatingHeader';
import { TNewbuildingsCompareViewRow } from '../NewbuildingsComparePage';
import { useCompareCards } from './hooks/useCompareCards';
import { usePinFlipAnimation } from './hooks/usePinFlipAnimation';
import { CompareCard } from './parts/CompareCard';
import { FloatingHeader } from './parts/FloatingHeader';
import { HeaderRow } from './parts/HeaderRow';
import * as style from './style.css';

const MAX_COLUMNS = 10;

export interface INewbuildingsCompareMobilePageProps {
  title: string;
  /** Карточки ЖК (по одной на колонку). Индексы совпадают с индексами ячеек в строках. */
  columns: React.ReactNode[];
  /** Компактные карточки ЖК для плавающей шапки (по одной на колонку, те же индексы). */
  floatingColumns: React.ReactNode[];
  rows: TNewbuildingsCompareViewRow[];
  /** Индекс закреплённой колонки ЖК или null. */
  pinnedIndex: number | null;
}

export const NewbuildingsCompareMobilePage = ({
  title,
  columns,
  floatingColumns,
  rows,
  pinnedIndex,
}: INewbuildingsCompareMobilePageProps) => {
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const floatingHeaderScrollRef = React.useRef<HTMLDivElement | null>(null);
  const headerRowRef = React.useRef<HTMLDivElement | null>(null);

  const { isFloatingHeaderMounted, isFloatingHeaderVisible, syncFloatingHeaderScroll } = useFloatingHeader({
    floatingHeaderScrollRef,
    headerRowRef,
    scrollRef,
  });

  const { handlePinToggleCapture, registerCell } = usePinFlipAnimation({ pinnedIndex, scrollRef });

  const cards = useCompareCards(rows);

  const handleScroll = React.useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      syncFloatingHeaderScroll(event.currentTarget.scrollLeft);
    },
    [syncFloatingHeaderScroll],
  );

  const columnStyle = React.useMemo(() => ({ '--columns': columns.length }) as React.CSSProperties, [columns.length]);

  const showAddButton = columns.length < MAX_COLUMNS;

  return (
    <div className={style['page']}>
      <div
        className={style['container']}
        data-testid="newbuildings-compare-mobile_page"
        onClickCapture={handlePinToggleCapture}
        style={columnStyle}
      >
        <div className={style['title']}>
          <UIHeading1 as="h1">{title}</UIHeading1>
        </div>

        {isFloatingHeaderMounted && (
          <FloatingHeader
            columns={floatingColumns}
            isVisible={isFloatingHeaderVisible}
            pinnedIndex={pinnedIndex}
            registerCell={registerCell}
            scrollerRef={floatingHeaderScrollRef}
            showAddButton={showAddButton}
            title={title}
          />
        )}

        <div
          ref={scrollRef}
          className={style['table-scroller']}
          data-testid="newbuildings-compare-mobile_table"
          onScroll={handleScroll}
          role="region"
          tabIndex={0}
        >
          <div className={style['table']} role="table">
            <div className={style['card-header']} role="rowgroup">
              <div className={style['card-header-content']} role="presentation">
                <HeaderRow
                  columns={columns}
                  pinnedIndex={pinnedIndex}
                  registerCell={registerCell}
                  rowRef={headerRowRef}
                  showAddButton={showAddButton}
                />
              </div>
            </div>

            {cards.map(card => (
              <CompareCard card={card} key={card.key} pinnedIndex={pinnedIndex} registerCell={registerCell} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
